import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Notification.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faBell } from '@fortawesome/pro-light-svg-icons';
import NotificationPanel from './NotificationPanel';
import { NotificationRecord } from './NotificationTypes';
import { getNotificationRoute } from './NotificationUtils';
import useNotifications from '../../hooks/useNotifications';

const NotificationComponent: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    isLoading,
    isLoadingMore,
    isEnabled,
    lastReceivedAt,
    loadMore,
    markAsRead,
  } = useNotifications();

  // Remounting the icon restarts the CSS animation, so back-to-back
  // notifications each get their own shake instead of sharing one.
  const [shakeKey, setShakeKey] = useState(0);

  useEffect(() => {
    if (!lastReceivedAt) return;
    setShakeKey((key) => key + 1);
  }, [lastReceivedAt]);

  // Close on an outside click or Escape.
  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (event: MouseEvent) => {
      const wrapper = wrapperRef.current;
      if (wrapper && !wrapper.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleSelect = (item: NotificationRecord) => {
    // Mark read regardless of whether there's somewhere to go -- clicking a
    // system_alert should still clear its dot.
    markAsRead(item.id);
    setIsOpen(false);

    const route = getNotificationRoute(item);
    if (route) navigate(route);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsOpen((open) => !open);
    }
  };

  // Roles without notifications (Workspace Owner) keep the bell visible but
  // inert: no click, no keyboard focus, no badge, no panel. The hook has
  // already skipped the socket and the list call for them.
  const isDisabled = !isEnabled;

  const bellClasses = ['right-aligned-bell'];
  if (isDisabled) bellClasses.push('right-aligned-bell-disabled');
  else if (isOpen) bellClasses.push('right-aligned-bell-active');

  return (
    <div className='notification-wrapper' ref={wrapperRef}>
      <div
        className={bellClasses.join(' ')}
        role='button'
        tabIndex={isDisabled ? -1 : 0}
        aria-disabled={isDisabled}
        aria-haspopup={isDisabled ? undefined : 'true'}
        aria-expanded={isDisabled ? undefined : isOpen}
        aria-label={
          isDisabled
            ? 'Notifications unavailable for this view'
            : (unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications')
        }
        onClick={isDisabled ? undefined : () => setIsOpen((open) => !open)}
        onKeyDown={isDisabled ? undefined : handleKeyDown}
      >
        <span
          key={shakeKey}
          className={shakeKey > 0 ? 'notification-bell-icon shake-bell' : 'notification-bell-icon'}
        >
          <FontAwesomeIcon icon={faBell as IconProp} color='#6b7280' fontSize={'18px'} />
        </span>
        {!isDisabled && unreadCount > 0 && <span className='alert-bell' />}
      </div>
      {!isDisabled && isOpen && (
        <NotificationPanel
          notifications={notifications}
          unreadCount={unreadCount}
          isLoading={isLoading}
          isLoadingMore={isLoadingMore}
          onSelect={handleSelect}
          onLoadMore={loadMore}
        />
      )}
    </div>
  );
};

export default NotificationComponent;
